import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Save, RefreshCw, Plus, Trash2, Calendar, Clock, Coffee, Sun, Moon } from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';

const DAYS = [
  { day: 1, name: 'Lundi' },
  { day: 2, name: 'Mardi' },
  { day: 3, name: 'Mercredi' },
  { day: 4, name: 'Jeudi' },
  { day: 5, name: 'Vendredi' },
  { day: 6, name: 'Samedi' },
  { day: 7, name: 'Dimanche' }
];

const CMSSchedulePage = () => {
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [specialModal, setSpecialModal] = useState({ open: false, data: null, index: null });

  useEffect(() => { loadSchedule(); }, []);

  const loadSchedule = async () => {
    try {
      const res = await api.get('/cms/schedule');
      // Ensure all days have the new format with morning/afternoon
      const regular = DAYS.map(d => {
        const existing = res.data?.regular?.find(r => r.day === d.day) || {};
        return {
          day: d.day,
          name: d.name,
          is_open: existing.is_open ?? (d.day <= 5),
          // Morning hours
          morning_open: existing.morning_open || existing.open || '09:00',
          morning_close: existing.morning_close || '12:00',
          // Afternoon hours
          afternoon_open: existing.afternoon_open || '14:00',
          afternoon_close: existing.afternoon_close || existing.close || '18:00',
          // Has lunch break?
          has_break: existing.has_break ?? true
        };
      });
      setSchedule({ ...res.data, regular });
    } catch (error) { 
      // Initialize with defaults if error
      const defaultRegular = DAYS.map(d => ({
        day: d.day,
        name: d.name,
        is_open: d.day <= 5,
        morning_open: '09:00',
        morning_close: '12:00',
        afternoon_open: '14:00',
        afternoon_close: '18:00',
        has_break: true
      }));
      setSchedule({ regular: defaultRegular, special: [] });
    }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/cms/schedule', schedule);
      toast.success('Horaires mis à jour');
    } catch (error) { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const updateRegularDay = (index, field, value) => {
    const newRegular = [...schedule.regular];
    newRegular[index] = { ...newRegular[index], [field]: value };
    setSchedule({ ...schedule, regular: newRegular });
  };

  const addSpecialDate = () => {
    setSpecialModal({ 
      open: true, 
      data: { 
        date: '', 
        name: '', 
        is_closed: true, 
        morning_open: '09:00',
        morning_close: '12:00',
        afternoon_open: '14:00',
        afternoon_close: '18:00',
        has_break: true
      }, 
      index: null 
    });
  };

  const saveSpecialDate = () => {
    const newSpecial = [...(schedule.special || [])];
    if (specialModal.index !== null) {
      newSpecial[specialModal.index] = specialModal.data;
    } else {
      newSpecial.push(specialModal.data);
    }
    setSchedule({ ...schedule, special: newSpecial });
    setSpecialModal({ open: false, data: null, index: null });
  };

  const deleteSpecialDate = (index) => {
    const newSpecial = schedule.special.filter((_, i) => i !== index);
    setSchedule({ ...schedule, special: newSpecial });
  };

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="h-8 w-8 animate-spin text-cyan-500" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Horaires</h1>
          <p className="text-slate-500">Gérez vos horaires d'ouverture avec pause déjeuner</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-gradient-to-r from-cyan-500 to-blue-600">
          {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Horaires réguliers
          </CardTitle>
          <CardDescription>Horaires d'ouverture de la semaine avec pause déjeuner</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {schedule?.regular?.map((day, index) => (
              <div key={day.day} className="p-4 rounded-lg bg-slate-50 border border-slate-100">
                {/* Day header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-slate-900 w-24">{day.name}</span>
                    <Switch
                      checked={day.is_open}
                      onCheckedChange={(checked) => updateRegularDay(index, 'is_open', checked)}
                    />
                    <Badge variant={day.is_open ? 'default' : 'secondary'}>
                      {day.is_open ? 'Ouvert' : 'Fermé'}
                    </Badge>
                  </div>
                  {day.is_open && (
                    <div className="flex items-center gap-2">
                      <Label className="text-sm text-slate-500">Pause déjeuner</Label>
                      <Switch
                        checked={day.has_break}
                        onCheckedChange={(checked) => updateRegularDay(index, 'has_break', checked)}
                      />
                    </div>
                  )}
                </div>
                
                {/* Hours */}
                {day.is_open && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                    {/* Morning */}
                    <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-100">
                      <Sun className="w-5 h-5 text-amber-500 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs font-medium text-amber-700 mb-1">Matin</p>
                        <div className="flex items-center gap-2">
                          <Input
                            type="time"
                            value={day.morning_open || ''}
                            onChange={(e) => updateRegularDay(index, 'morning_open', e.target.value)}
                            className="w-28 h-8 text-sm"
                          />
                          <span className="text-slate-400">-</span>
                          <Input
                            type="time"
                            value={day.morning_close || ''}
                            onChange={(e) => updateRegularDay(index, 'morning_close', e.target.value)}
                            className="w-28 h-8 text-sm"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Lunch break indicator */}
                    {day.has_break && (
                      <div className="hidden md:flex items-center justify-center absolute left-1/2 -translate-x-1/2" style={{display: 'none'}}>
                        <div className="flex items-center gap-2 px-3 py-1 bg-slate-200 rounded-full">
                          <Coffee className="w-4 h-4 text-slate-500" />
                          <span className="text-xs text-slate-500">Pause</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Afternoon */}
                    <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
                      <Moon className="w-5 h-5 text-blue-500 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs font-medium text-blue-700 mb-1">Après-midi</p>
                        <div className="flex items-center gap-2">
                          <Input
                            type="time"
                            value={day.afternoon_open || ''}
                            onChange={(e) => updateRegularDay(index, 'afternoon_open', e.target.value)}
                            className="w-28 h-8 text-sm"
                            disabled={!day.has_break}
                          />
                          <span className="text-slate-400">-</span>
                          <Input
                            type="time"
                            value={day.afternoon_close || ''}
                            onChange={(e) => updateRegularDay(index, 'afternoon_close', e.target.value)}
                            className="w-28 h-8 text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Summary when has break */}
                {day.is_open && day.has_break && (
                  <div className="mt-3 flex items-center justify-center gap-2 text-sm text-slate-500">
                    <Coffee className="w-4 h-4" />
                    <span>Pause de {day.morning_close} à {day.afternoon_open}</span>
                  </div>
                )}
                
                {/* No break - continuous hours */}
                {day.is_open && !day.has_break && (
                  <div className="mt-3 text-center text-sm text-slate-500">
                    Horaires continus : {day.morning_open} - {day.afternoon_close}
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Horaires spéciaux
              </CardTitle>
              <CardDescription>Jours fériés, vacances, événements...</CardDescription>
            </div>
            <Button onClick={addSpecialDate} variant="outline">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {schedule?.special?.length === 0 ? (
            <p className="text-sm text-slate-500 italic text-center py-4">Aucune période spéciale configurée</p>
          ) : (
            <div className="space-y-2">
              {schedule?.special?.map((special, index) => {
                const startDate = special.date_start || special.date;
                const endDate = special.date_end;
                const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : '';
                const dateDisplay = endDate && endDate !== startDate 
                  ? `${formatDate(startDate)} → ${formatDate(endDate)}`
                  : formatDate(startDate);
                
                return (
                  <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                    <div className="flex items-center gap-4 flex-wrap">
                      <Badge variant={special.is_closed ? 'destructive' : 'default'}>
                        {special.is_closed ? 'Fermé' : 'Horaires modifiés'}
                      </Badge>
                      <span className="font-semibold text-slate-900">{special.name}</span>
                      <span className="text-sm text-slate-600 bg-slate-200 px-2 py-0.5 rounded">{dateDisplay}</span>
                      {!special.is_closed && (
                        <span className="text-sm text-slate-500">
                          {special.morning_open}-{special.morning_close} / {special.afternoon_open}-{special.afternoon_close}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => setSpecialModal({ open: true, data: { ...special, date_start: special.date_start || special.date }, index })}>
                        Modifier
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteSpecialDate(index)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Special Date Modal */}
      <Dialog open={specialModal.open} onOpenChange={(open) => !open && setSpecialModal({ open: false, data: null, index: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{specialModal.index !== null ? 'Modifier' : 'Ajouter'} une période spéciale</DialogTitle>
          </DialogHeader>
          {specialModal.data && (
            <div className="space-y-4">
              {/* Titre de la période */}
              <div className="space-y-2">
                <Label>Titre de la période *</Label>
                <Input
                  value={specialModal.data.name}
                  onChange={(e) => setSpecialModal(prev => ({ ...prev, data: { ...prev.data, name: e.target.value } }))}
                  placeholder="Ex: Vacances de Noël, Fermeture estivale..."
                />
              </div>
              
              {/* Dates de début et fin */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date de début *</Label>
                  <Input
                    type="date"
                    value={specialModal.data.date_start || specialModal.data.date || ''}
                    onChange={(e) => setSpecialModal(prev => ({ ...prev, data: { ...prev.data, date_start: e.target.value, date: e.target.value } }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date de fin</Label>
                  <Input
                    type="date"
                    value={specialModal.data.date_end || ''}
                    onChange={(e) => setSpecialModal(prev => ({ ...prev, data: { ...prev.data, date_end: e.target.value } }))}
                  />
                  <p className="text-xs text-slate-500">Laissez vide pour un seul jour</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Switch
                  checked={specialModal.data.is_closed}
                  onCheckedChange={(checked) => setSpecialModal(prev => ({ ...prev, data: { ...prev.data, is_closed: checked } }))}
                />
                <Label>Fermé pendant cette période</Label>
              </div>
              
              {!specialModal.data.is_closed && (
                <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm font-medium text-slate-700">Horaires modifiés</p>
                  
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={specialModal.data.has_break}
                      onCheckedChange={(checked) => setSpecialModal(prev => ({ ...prev, data: { ...prev.data, has_break: checked } }))}
                    />
                    <Label className="text-sm">Avec pause déjeuner</Label>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-500">Matin</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="time"
                          value={specialModal.data.morning_open}
                          onChange={(e) => setSpecialModal(prev => ({ ...prev, data: { ...prev.data, morning_open: e.target.value } }))}
                          className="w-24"
                        />
                        <span>-</span>
                        <Input
                          type="time"
                          value={specialModal.data.morning_close}
                          onChange={(e) => setSpecialModal(prev => ({ ...prev, data: { ...prev.data, morning_close: e.target.value } }))}
                          className="w-24"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-500">Après-midi</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="time"
                          value={specialModal.data.afternoon_open}
                          onChange={(e) => setSpecialModal(prev => ({ ...prev, data: { ...prev.data, afternoon_open: e.target.value } }))}
                          className="w-24"
                        />
                        <span>-</span>
                        <Input
                          type="time"
                          value={specialModal.data.afternoon_close}
                          onChange={(e) => setSpecialModal(prev => ({ ...prev, data: { ...prev.data, afternoon_close: e.target.value } }))}
                          className="w-24"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSpecialModal({ open: false, data: null, index: null })}>
              Annuler
            </Button>
            <Button onClick={saveSpecialDate} disabled={!specialModal.data?.name || !specialModal.data?.date_start}>
              <Save className="w-4 h-4 mr-2" /> Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CMSSchedulePage;
